/**
 * Sync API — the single import for all synced data.
 *
 * Define entities once here. Import `sync` anywhere.
 *
 * ```ts
 * import { sync } from "./sync";
 *
 * // Reactive read:
 * const folders = sync.folders.use();      // SyncFolder[]
 *
 * // Mutations (auto-synced):
 * sync.folders.add({ id: "1", name: "Math" });
 * sync.folders.update("1", { name: "Science" });
 * sync.settings.set({ preset: "custom" });
 *
 * // Custom entities:
 * const decks = sync.decks.use();
 * sync.decks.updateCards("myDeck", cards => [...cards, newCard]);
 * ```
 */

import { getRandomDeckEmoji } from '@flashka/shared/deck/defaults';
import { mindmapRecordKey } from '@flashka/shared/deck/materials/deck-items';
import {
  buildDeckShareUpdate,
  buildDeckUnshareUpdate,
} from '@flashka/shared/deck/sharing/operations';
import type { DeckSourceDraft } from '@flashka/shared/deck/sources/build';
import { keepPinnedQuizDocuments } from '@flashka/shared/deck/sources/primary-pin';
import type { OralExamAttemptRecord } from '@flashka/shared/oral-exam/oral-exam-attempt-record';
import {
  buildShareUpdate,
  buildUnshareUpdate,
} from '@flashka/shared/quiz/sharing/operations';
import { entitySchema } from '@flashka/shared/sync-engine/schema/entities';
import type {
  DeckId,
  DeckMaterialsIndex,
  DeckPath,
  DeckShareOptions,
  DeckSource,
  SyncAnnotationsRecordEntry,
  SyncChatConversationEntry,
  SyncDeckMindmapEntry,
  SyncPdfAnnotations,
  SyncQuizProgressEntry,
} from '@flashka/shared/sync-engine/schema/shared-types';
import type { SyncCardProps as CardProps } from '@flashka/shared/sync-engine/schema/shared-types';
import {
  asDeckId,
  asDeckPath,
  quizKey,
} from '@flashka/shared/sync-engine/schema/shared-types';

import { v4 as uuidv4 } from 'uuid';

import {
  bumpToday,
  completeSessionToday,
  type CounterField,
  createSessionCompletionGate,
} from './activity/derive';
import { firstLiveDeckIdForPath } from './deck-path-resolve';
import { folderHideStampISO } from './folder-normalize';
import { createSyncApi, custom } from './internal/framework/create-sync-api';
import { def } from './internal/framework/entity-def-helper';
import { useFlashkaStore } from './internal/store/flashka-store';
import type { DeckProperties } from './provider/sync-storage';
import { createScopedDeck, type ScopedDeck } from './scoped-deck';
import { studyGroups as studyGroupsApi } from './study-groups';

/** Input for `sync.decks.createNew()`. */
export type CreateNewDeckInput = {
  /** The server-side deck identifier (becomes the first deckPath). */
  deckName: string;
  displayName: string;
  folder: string;
  color?: string;
  imageBackground?: string;
  /**
   * Emoji/tag tile. OMIT it (undefined) and the deck is born with a random
   * {@link getRandomDeckEmoji} — every creation surface gets a characterful
   * tile for free. Pass `''` to deliberately keep it blank (that's what a
   * form whose emoji field the user cleared sends).
   */
  thumbnailText?: string;
  /** Per-card language defaults. Caller is responsible for sourcing these
   *  (e.g. from user settings); sync stays platform-neutral. */
  languageSettings?: { front: string; back: string };
  /** Initial cards. Defaults to an empty deck. */
  cards?: CardProps[];
  /**
   * Deck-level source overview to carry onto the new deck — what the deck was
   * built from (a PDF, a transcript, pasted text, a described topic, an APKG
   * import), written at creation by every "+" flow on both platforms so the
   * deck never has to ask the server what its own source is.
   *
   * Build entries with `buildNewDeckSource` (`@flashka/shared/deck/sources`), and
   * leave `sourceId` off: an entry without one is stamped with the deck id
   * minted below, which is how the FIRST source on a deck is addressed. Entries
   * that already carry a `sourceId` pass through untouched — a copy path
   * (importing a shared deck, forking an SG mirror) must keep the ORIGIN ids,
   * because `planDeckMerge` dedupes by `sourceId`.
   */
  sources?: DeckSourceDraft[];
  /** The copied deck deliberately has no document (see `SyncDeckMeta`). */
  sourcesClearedAtISO?: string;
  /** The copied deck's notes/maps list (see `SyncDeckMeta.materials`). */
  materials?: DeckMaterialsIndex;
};

// ---------------------------------------------------------------------------
// Custom entity API types (deck + quiz have bespoke mutations)
// ---------------------------------------------------------------------------

type DeckApi = {
  /**
   * All cards as normalized map: [DeckId][cardId] → CardProps (reactive).
   *
   * Pass a `selector` to narrow the subscription: the component re-renders
   * only when the selector's return value changes (Object.is). Callers that
   * don't need the whole map (e.g. single-deck reads, or screens that want a
   * stable empty slice) can return a constant to opt out of card-write
   * re-renders entirely.
   */
  use<T = Record<DeckId, Record<string, CardProps>>>(
    selector?: (cards: Record<DeckId, Record<string, CardProps>>) => T,
  ): T;
  /** All cards as normalized map (snapshot). */
  get(): Record<DeckId, Record<string, CardProps>>;
  /**
   * All deck properties keyed by DeckId (reactive).
   *
   * Pass a `selector` to narrow the subscription (mirrors {@link use}): the
   * component re-renders only when the selector's return value changes
   * (Object.is). Single-deck readers should select their one deck's props so
   * an unrelated deck's write can't churn an unrelated screen.
   *
   * Declared as overloads (selector first, bare last) so the no-arg form's
   * concrete return type is what `ReturnType<typeof …useProperties>` resolves
   * to — a single generic signature would degrade that to `unknown`.
   */
  useProperties<T>(selector: (props: Record<DeckId, DeckProperties>) => T): T;
  useProperties(): Record<DeckId, DeckProperties>;
  /** All deck properties keyed by DeckId (snapshot). */
  getProperties(): Record<DeckId, DeckProperties>;
  /** Create a brand-new deck. Generates id/deckPath/timestamps internally
   *  and writes properties + cards in one operation. Returns the new id. */
  createNew(input: CreateNewDeckInput): DeckId;
  /** Set/replace a deck's cards from an array (sync boundary). */
  setDeck(id: DeckId, cards: CardProps[], updatedAtISO?: string): void;
  /** Set/replace a deck's properties. */
  setProperties(id: DeckId, props: DeckProperties): void;
  /** Soft-delete a deck (cascades to sources/PDFs/notes). */
  remove(id: DeckId): void;
  /** Transform a deck's cards (escape hatch for multi-card operations). */
  updateCards(id: DeckId, transform: (cards: CardProps[]) => CardProps[]): void;
  /** Move deck to a folder. */
  moveToFolder(id: DeckId, folderId: string | undefined): void;
  /** Reorder deck within its folder (fractional sortKey). */
  reorder(id: DeckId, sortKey: string): void;
  /** Batch-update properties for multiple decks (one sync tick). */
  batchUpdateProperties(
    updates: Array<{ id: DeckId; partial: Partial<DeckProperties> }>,
  ): void;
  /** Get a scoped API for a single deck by UUID. */
  deck(id: DeckId): ScopedDeck;
  /** Find a deck by its deckPath (legacy name). Returns the first match. */
  findByDeckPath(path: DeckPath): ScopedDeck | undefined;
  /**
   * Authorise link-sharing this deck (`/d/:code`). Mints a code locally and
   * writes `shareCode` + `linkSharedAtISO` + `shareOptions` on the deck
   * meta; the regular sync push carries the change to the owner's UserDo,
   * which mirrors `shareCode` → `{userId, deckId}` into the `SHARE_DECK_KV`
   * index (see `afterHandled` in flashka-web/party/server/userdo.ts).
   *
   * Idempotent on identical options (existing code reused); a re-share with
   * different toggles reuses the code but refreshes `shareOptions`. Returns
   * the active shareCode, or null if the deck is missing/tombstoned.
   *
   * Owner-only: callers must gate the UI so a study-group MEMBER copy
   * (`meta.originUserId` set) can't link-share — share-deck.tsx shows an
   * owner-only note for those instead of calling this.
   */
  share(id: DeckId, options: DeckShareOptions): string | null;
  /** Revoke the link-share. Clears `shareCode`/`linkSharedAtISO`/
   *  `shareOptions`; the DO mirrors the deletion into the KV index. */
  unshare(id: DeckId): void;
};

// QuizApi extensions (beyond the auto-generated record CRUD)

// ---------------------------------------------------------------------------
// Custom entity implementations (delegate to Zustand store)
// ---------------------------------------------------------------------------

const deckApi: DeckApi = {
  use: (<T = Record<DeckId, Record<string, CardProps>>>(
    selector?: (cards: Record<DeckId, Record<string, CardProps>>) => T,
  ): T =>
    useFlashkaStore((s) =>
      selector ? selector(s.cards) : (s.cards as unknown as T),
    )) as DeckApi['use'],
  get: () => useFlashkaStore.getState().cards,
  useProperties: (<T = Record<DeckId, DeckProperties>>(
    selector?: (props: Record<DeckId, DeckProperties>) => T,
  ): T =>
    useFlashkaStore((s) =>
      selector ? selector(s.deckProperties) : (s.deckProperties as unknown as T),
    )) as DeckApi['useProperties'],
  getProperties: () => useFlashkaStore.getState().deckProperties,
  createNew: ({
    deckName,
    displayName,
    folder,
    color,
    imageBackground,
    thumbnailText,
    languageSettings,
    cards = [],
    sources,
    sourcesClearedAtISO,
    materials,
  }) => {
    const id = asDeckId(uuidv4());
    const now = new Date().toISOString();
    // Stamp the deck's OWN source with the deck id. `sourceId === deckId` is
    // the addressing rule for a deck's first source (card refs resolve
    // unqualified refs against it, `get-ai-deck-source` answers by it), and a
    // creation-time caller cannot know the id — it is minted right here.
    const stampedSources = sources?.map((source) =>
      source.sourceId ? (source as DeckSource) : { ...source, sourceId: id },
    );
    const meta: DeckProperties = {
      id,
      deckPaths: [asDeckPath(deckName)],
      displayName,
      color,
      imageBackground,
      // `??`, not `||`: an explicit '' is the user clearing the field.
      thumbnailText: thumbnailText ?? getRandomDeckEmoji(),
      folder,
      languageSettings,
      sources: stampedSources,
      ...(sourcesClearedAtISO ? { sourcesClearedAtISO } : {}),
      ...(materials ? { materials } : {}),
      addedTimeISO: now,
      updatedAtISO: now,
    };
    const store = useFlashkaStore.getState();
    store.setDeckProperty(id, meta);
    store.setDeckFromSync(id, cards, now);
    return id;
  },
  setDeck: (...args) => useFlashkaStore.getState().setDeckFromSync(...args),
  setProperties: (...args) => useFlashkaStore.getState().setDeckProperty(...args),
  // Route through the scoped-deck facade so the delete cascades to Yjs
  // (notes / annotations / handwriting / embeds sub-map) without every
  // caller having to know about `deleteDeckNotes`.
  remove: (id) => createScopedDeck(id).remove(),
  updateCards: (...args) => useFlashkaStore.getState().updateDeckCards(...args),
  moveToFolder: (...args) => useFlashkaStore.getState().moveDeckToFolder(...args),
  reorder: (...args) => useFlashkaStore.getState().reorderDeck(...args),
  batchUpdateProperties: (...args) =>
    useFlashkaStore.getState().batchUpdateDeckProperties(...args),
  deck: (id) => createScopedDeck(id),
  // LIVE-first, not raw insertion order: a deck path is not unique (a
  // recovered [BACKUP] deck historically kept the original's deckPaths),
  // and this is a WRITE-side resolver — the raw scan could hand a rename
  // the tombstoned twin while every read surface showed the live one, so
  // the edit silently landed on a dead deck. See deck-path-resolve.ts.
  findByDeckPath: (path) => {
    const props = useFlashkaStore.getState().deckProperties;
    const id = firstLiveDeckIdForPath(props, path);
    return id ? createScopedDeck(asDeckId(id)) : undefined;
  },
  share: (id, options) => {
    const meta = useFlashkaStore.getState().deckProperties[id];
    const { next, code } = buildDeckShareUpdate(meta, options);
    // `setDeckProperty` auto-stamps `updatedAtISO`, so the push fires.
    if (next) useFlashkaStore.getState().setDeckProperty(id, next);
    return code;
  },
  unshare: (id) => {
    const meta = useFlashkaStore.getState().deckProperties[id];
    const next = buildDeckUnshareUpdate(meta);
    if (next) useFlashkaStore.getState().setDeckProperty(id, next);
  },
};

// ---------------------------------------------------------------------------
// Schema definition — THE single source of truth for the client API
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Store-level accessors (hydration state, not entity-specific)
// ---------------------------------------------------------------------------

type StoreApi = {
  /** Whether the store has been hydrated from IndexedDB. */
  useIsHydrated(): boolean;
  /** Current signed-in user ID. */
  useCurrentUserId(): string | null;
};

const storeApi: StoreApi = {
  useIsHydrated: () => useFlashkaStore((s) => s.isHydrated),
  useCurrentUserId: () => useFlashkaStore((s) => s.currentUserId),
};

// ---------------------------------------------------------------------------
// Schema definition — THE single source of truth for the client API
// ---------------------------------------------------------------------------

/** Once-per-attempt gate behind `sync.studyDays.completeSession`. */
const firstSessionCompletion = createSessionCompletionGate();

export const sync = createSyncApi({
  store: custom<StoreApi>(storeApi),

  // ── Schema-defined entities (def() pulls type/key/primaryKey from config) ──
  //
  // Extensions are declared as `(ops, self) => ({...})`. `ops` is the
  // mutation surface (`get/set/add/update/remove`); those calls go through
  // the framework's auto-generated CRUD so they stamp `updatedAtISO`,
  // persist to IndexedDB, and push to the server automatically.
  folders: def(entitySchema.folders, {
    extensions: (ops) => ({
      moveToParent: (id: string, newParentId: string | null) =>
        ops.update(id, { parentFolder: newParentId }),
      reorderFolder: (id: string, sortKey: string) => ops.update(id, { sortKey }),
      /**
       * Re-parent AND re-order in ONE update, for a sidebar drop that changes
       * both. As two calls it was two persisted writes: a crash between them
       * left the folder in its new parent still holding the sortKey minted for
       * its OLD level's neighbours — a position the user never chose, and one
       * nothing later repairs. A single `ops.update` cannot tear that way.
       */
      moveAndReorder: (id: string, newParentId: string | null, sortKey: string) =>
        ops.update(id, { parentFolder: newParentId, sortKey }),
      /**
       * Manual folder hide/restore — the folder sibling of
       * `useDeckArchiveActions`. A hidden folder leaves the sidebar, the
       * dashboard grid, search and every folder picker on BOTH clients, and
       * comes back only from Settings -> Archive.
       *
       * Lives here rather than in a hook because, unlike the deck version,
       * there is no activity row to bump — it is a one-field write, and both
       * clients must make exactly the same one. Writing `null` (not
       * `undefined`) on restore is the wire convention documented on
       * `UserDo.folders.archivedTimeISO`: `undefined` = never synced,
       * `null` = explicitly cleared.
       */
      archive: (id: string) => ops.update(id, { archivedTimeISO: folderHideStampISO() }),
      unarchive: (id: string) => ops.update(id, { archivedTimeISO: null }),
    }),
  }),
  todos: def(entitySchema.todos, {
    extensions: (ops) => ({
      markCompleted: (id: string, atISO: string = new Date().toISOString()) =>
        ops.update(id, { lastCompletedISO: atISO }),
      resetCompletion: (id: string) => ops.update(id, { lastCompletedISO: undefined }),
      reorder: (id: string, sortKey: string) => ops.update(id, { sortKey }),
    }),
  }),
  calendarTasks: def(entitySchema.calendarTasks, {
    extensions: (ops) => ({
      setDone: (id: string, done: boolean) =>
        ops.update(id, { done, updatedAtISO: new Date().toISOString() }),
      reorder: (id: string, sortKey: string) => ops.update(id, { sortKey }),
    }),
  }),
  settings: def(entitySchema.settings),
  userInfo: def(entitySchema.userInfo),
  // Private consent-first social state (birthYear/consent + dialog throttles).
  // Read/written on web via sync.socialConsent.use()/.get()/.set(); roams across
  // devices and hydrates locally so the age gate never flashes on reload.
  socialConsent: def(entitySchema.socialConsent),
  exploreProgress: def(entitySchema.exploreProgress),
  // Coach marks met, across devices (client-authored; `coach-marks/seen.ts`).
  coachMarksSeen: def(entitySchema.coachMarksSeen),
  // FTUE v2 guided first session (client-authored; `ftue/machine.ts`).
  ftueSession: def(entitySchema.ftueSession),
  dashboardWidgets: def(entitySchema.dashboardWidgets),
  // Read-only credit rollup (server-authored). Clients only ever read it
  // (`sync.credits.use()`); writes are rejected by UserDo.authorize.
  credits: def(entitySchema.credits),
  // Story 11 — global notifications inbox (server-authored). Clients read
  // `sync.notifications.use()` to render the bell panel; writes come from
  // UserDo's /internal/notifications/{push,mark-seen} endpoints only.
  notifications: def(entitySchema.notifications),
  // Per-item read marks for the notification centre. CLIENT-authored, unlike
  // `notifications` above — the bell writes it when a row is dismissed.
  notificationsRead: def(entitySchema.notificationsRead),
  // The notifications screen's platform rows (server-authored) — merged into
  // the bell as "From Ka" rows by `dashboard/notifications/inbox.ts`.
  inboxMessages: def(entitySchema.inboxMessages),
  // The study-reminder switch + zone (client-authored). UserDo reads it to
  // time and send study-plan reminders.
  studyReminders: def(entitySchema.studyReminders),
  // Read-only plan mirror (server-authored). Clients read via usePlan();
  // writes are rejected by UserDo.authorize.
  plan: def(entitySchema.plan),
  quizzes: def(entitySchema.quizProgress, {
    extensions: (ops) => ({
      // A session writes the whole entry back on every answer, from its own
      // state: an attempt pinned to its document in the store meanwhile (the
      // deck's documents moved) keeps that document — see the planner.
      setProgress: (entry: SyncQuizProgressEntry) =>
        ops.set(
          keepPinnedQuizDocuments(entry, ops.get()[quizKey(entry.deckId, entry.quizTs)]),
        ),
      deleteProgress: (deckId: string, quizTs_: string) =>
        ops.remove(quizKey(deckId, quizTs_)),
      // Nested mutation: update `quizName` on the last attempt of a given
      // entry. Read-modify-write via `ops.get()` + `ops.set()`. (If more
      // nested-array cases like this appear we can introduce an
      // `ops.mutate(draft => …)` Immer helper.)
      updateName: (deckId: string, quizTs_: string, name: string) => {
        const key = quizKey(deckId, quizTs_);
        const entry = ops.get()[key];
        if (!entry?.attempts?.length) return;
        const attempts = entry.attempts.slice();
        attempts[attempts.length - 1] = {
          ...attempts[attempts.length - 1],
          quizName: name,
        };
        ops.set({ ...entry, attempts });
      },

      /**
       * Append one oral-exam attempt to its entry (creating the entry on
       * the first take), keyed by (deckId, quizTs). Read-modify-write so a
       * RETAKE appends to the same `attempts[]` instead of overwriting the
       * earlier takes — `quizTs` is the FIRST attempt's `ts`, passed
       * verbatim on every retake. Oral attempts are an opaque payload in
       * the shared entity (validated as `z.array(z.unknown())`).
       */
      appendOralAttempt: (params: {
        deckId: string;
        quizTs: string;
        attempt: OralExamAttemptRecord;
        userId?: string;
        isFolderQuiz?: boolean;
      }) => {
        const key = quizKey(params.deckId, params.quizTs);
        const existing = ops.get()[key];
        const prior = (existing?.attempts ?? []) as unknown as OralExamAttemptRecord[];
        ops.set({
          deckId: asDeckId(params.deckId),
          quizTs: params.quizTs,
          attempts: [
            ...prior,
            params.attempt,
          ] as unknown as SyncQuizProgressEntry['attempts'],
          updatedAtISO: new Date().toISOString(),
          examKind: 'oral',
          isFolderQuiz: params.isFolderQuiz ?? existing?.isFolderQuiz ?? false,
          userId: params.userId ?? existing?.userId,
        });
      },

      /**
       * Authorise sharing this quiz. Mints a code locally and writes
       * `sharedAt` + `shareCode` on the entry; the regular sync push
       * carries the change to the owner's UserDo, which mirrors the
       * shareCode into the `SHARE_QUIZ_KV` index via its binding (see
       * `afterHandled` in flashka-web/party/server/userdo.ts).
       *
       * Idempotent: if a code already exists on the entry it is reused
       * so previously-distributed `/q/{code}` URLs keep working.
       *
       * Returns the current shareCode, or null if the entry is missing
       * (e.g. tombstoned) — callers should typically branch on that.
       */
      share: (deckId: string, quizTs_: string): string | null => {
        const key = quizKey(deckId, quizTs_);
        const entry = ops.get()[key];
        const { next, code } = buildShareUpdate(entry);
        if (next) ops.set(next);
        return code;
      },

      /**
       * Revoke the share. Clears `sharedAt` + `shareCode` on the entry;
       * the DO mirrors the deletion into the KV index. Any in-flight
       * `/q/{code}` GET that races past the KV delete hits the recipient
       * route's 404+GC self-heal (the DO's `/internal/get-shared-quiz`
       * also returns 404 once `sharedAt` is cleared).
       */
      unshare: (deckId: string, quizTs_: string): void => {
        const key = quizKey(deckId, quizTs_);
        const entry = ops.get()[key];
        const next = buildUnshareUpdate(entry);
        if (next) ops.set(next);
      },
    }),
  }),

  // Per-day study counters (streak / heatmap / stats). Emitters are thin
  // wrappers over `bump`; add a new counter by widening `CounterField`
  // (activity/derive.ts) and adding one line here. Fast review has no emitter
  // of its own — its good/easy reviews call `studyCard` (spacedRepetitionStudy),
  // matching the server-side history backfill.
  //
  // Every emitter is cumulative per session slot and therefore safe under
  // the entity's per-session/per-field MAX merge (`mergeStudyDay`): retries,
  // offline writes, and concurrent devices never lose or double-count. New
  // counters (e.g. Story 9 mastery points) will land here as one more line.
  studyDays: def(entitySchema.studyDays, {
    extensions: (ops) => {
      const bump = (field: CounterField, n = 1): void => bumpToday(ops, field, n);
      return {
        /** SR + Fast Review good/easy reviews → streak + heatmap counter. */
        studyCard: (n = 1) => bump('spacedRepetitionStudy', n),
        /** Quiz "answered" count (any response !== 'none') → streak + heatmap. */
        studyQuiz: (n = 1) => bump('quizStudy', n),
        /**
         * One FINISHED study session — a quiz, mock exam, oral exam or case
         * study that reached its results → `sessionsCompleted`, which alone
         * makes today a streak day (`dayQualifiesForStreak`). NOT in
         * ACTIVITY_FIELDS: it is not a card-equivalent, so the heatmap does
         * not move. `attemptKey` names the attempt (kind + its own id); the
         * same key is credited once per app run however often it is called.
         * Returns whether it credited. Call sites go through
         * `recordStudySessionCompleted` (activity/session-completion.ts).
         */
        completeSession: (attemptKey: string): boolean =>
          completeSessionToday(ops, firstSessionCompletion, attemptKey),
        /**
         * Quiz CORRECT-answer count (Story 8) → the "Cards & questions"
         * lifetime tile. NOT in ACTIVITY_FIELDS: the streak/heatmap stay
         * ANSWERED-based so a bad-but-attempted day still counts toward
         * "did you show up?".
         */
        studyQuizCorrect: (n = 1) => bump('quizCorrect', n),
        /**
         * Live wall-clock study time in ms (Story 8) → the "Study time" tile
         * for days from `ACTIVITY_TRACKING_LAUNCH_DATE_KEY` onward. Callers pass a
         * monotonically-increasing high-water-mark DELTA (see
         * `createPersonalStudyTimeReporter`), which lands in the current
         * session slot as a cumulative sum — safe under MAX-merge.
         */
        studyTime: (ms: number) => bump('timeMs', ms),
        /**
         * Personal Mastery-Points ledger (Story 9) → the "Mastery Points"
         * profile tile + visitor snapshot. `centi` is integer CENTI-MP (1 MP
         * = 100), computed by the caller via `study-groups/scoring.ts`
         * (`scoreFor` / `scoreForOralExamSession` / `scoreForCaseStudySession`)
         * so per-event rounding never drifts — display MP rounds once per
         * aggregate. Independent of the per-group SGScore wire: this accrues on
         * EVERY deck (personal and group-linked alike), always.
         */
        studyMasteryPoints: (centi: number) => bump('masteryPointsCenti', centi),
      };
    },
  }),

  // Note: `groupDeckProgress` was removed in favour of Option F —
  // per-user study state (studyState, actions, starred, tags, notes,
  // chat) now lives directly on the linked local deck's cards and is
  // kept private by the field-scope router in
  // `sync-engine/schema/field-scopes.ts`.

  // ── Per-deck record entities (annotations only) ──
  //
  // Notes used to live here too. They've moved to the per-user Yjs doc;
  // access via `sync.decks.deck(id).notes.*` in `src/sync/scoped-deck.ts`.
  // Annotations stay as a record entity because they're PDF-only and
  // don't need CRDT semantics.
  //
  // `ops.set(entry)` inside setForDeck auto-stamps `updatedAtISO` and pushes;
  // `ops.remove(deckName)` cascades an `annotations-delete`.
  annotations: def(entitySchema.annotations, {
    extensions: (ops, self) => ({
      useForDeck: (deckName: string) =>
        self.use((all) => all[deckName]) as SyncAnnotationsRecordEntry | undefined,
      getForDeck: (deckName: string): SyncAnnotationsRecordEntry | undefined =>
        ops.get()[deckName],
      setForDeck: (deckName: string, annotations: SyncPdfAnnotations) =>
        ops.set({
          deckName,
          annotations,
          updatedAtISO: new Date().toISOString(),
        } as SyncAnnotationsRecordEntry),
      removeForDeck: (deckName: string) => ops.remove(deckName),
    }),
  }),

  // ── Deck mindmaps (study-set "Mindmaps" tab) ──
  //
  // One record per MAP — a single entry with the whole mindmap state (like
  // flashcards' deck record, NOT a Yjs doc like notes). `ops.set(entry)`
  // stamps `updatedAtISO` and pushes; `ops.remove(key)` cascades a
  // `deckMindmaps-delete`.
  //
  // The key is `mindmapRecordKey(deckKey, mindmapId)`: a deck's FIRST map is
  // stored under the bare deck key, exactly as when a deck could only have
  // one, and later maps hang off a `#<id>` suffix. So every map ever
  // generated still opens, with no migration and no schema change — the
  // entity never learns a deck can have several. `useAllForDeck` is the one
  // reader that has to know the convention.
  deckMindmaps: def(entitySchema.deckMindmaps, {
    extensions: (ops, self) => ({
      useForDeck: (deckName: string) =>
        self.use((all) => all[deckName]) as SyncDeckMindmapEntry | undefined,
      /**
       * Every map in the store, by record key. Callers group it per deck with
       * `mindmapIdFromRecordKey` inside their own `useMemo`.
       *
       * Deliberately NOT a per-deck selector: `self.use` compares with
       * `Object.is`, so a selector that BUILDS the deck's group returns a new
       * object on every store write and re-renders the whole deck screen on
       * every card edit. Returning the entity's own record hands back a
       * reference that changes only when a mind map does.
       */
      useAll: () => self.use((all) => all) as Record<string, SyncDeckMindmapEntry>,
      /** Read one map by id. See `mindmapRecordKey` for the key convention. */
      useById: (deckKey: string, mindmapId: string) =>
        self.use((all) => all[mindmapRecordKey(deckKey, mindmapId)]) as
          | SyncDeckMindmapEntry
          | undefined,
      setById: (
        deckKey: string,
        mindmapId: string,
        data: Pick<SyncDeckMindmapEntry, 'mindmap' | 'layout' | 'graph'>,
      ) =>
        ops.set({
          deckName: mindmapRecordKey(deckKey, mindmapId),
          mindmap: data.mindmap,
          ...(data.layout !== undefined ? { layout: data.layout } : {}),
          ...(data.graph !== undefined ? { graph: data.graph } : {}),
          updatedAtISO: new Date().toISOString(),
        } as SyncDeckMindmapEntry),
      removeById: (deckKey: string, mindmapId: string) =>
        ops.remove(mindmapRecordKey(deckKey, mindmapId)),
      getForDeck: (deckName: string): SyncDeckMindmapEntry | undefined =>
        ops.get()[deckName],
      setForDeck: (
        deckName: string,
        data: Pick<SyncDeckMindmapEntry, 'mindmap' | 'layout' | 'graph'>,
      ) =>
        ops.set({
          deckName,
          mindmap: data.mindmap,
          ...(data.layout !== undefined ? { layout: data.layout } : {}),
          ...(data.graph !== undefined ? { graph: data.graph } : {}),
          updatedAtISO: new Date().toISOString(),
        } as SyncDeckMindmapEntry),
      removeForDeck: (deckName: string) => ops.remove(deckName),
    }),
  }),

  // ── Professor Ka chat conversations ──
  //
  // One record entry per conversation; `messages` holds the AI SDK message
  // list verbatim. `ops.set(entry)` auto-stamps `updatedAtISO` and pushes;
  // `ops.remove(id)` cascades a `chatConversations-delete`.
  chats: def(entitySchema.chatConversations, {
    extensions: (ops, self) => ({
      /** Reactive map of all conversations (sort in a useMemo at the call site). */
      useAll: () => self.use((all) => all) as Record<string, SyncChatConversationEntry>,
      /** Reactive single conversation by id. */
      useById: (id: string) =>
        self.use((all) => all[id]) as SyncChatConversationEntry | undefined,
      /** Snapshot lookup of a single conversation by id. */
      getById: (id: string): SyncChatConversationEntry | undefined => ops.get()[id],
      /** Snapshot of every conversation. Pairs with `useAll` for callers that
       *  need to pick a thread inside an event handler (e.g. resolving which
       *  conversation to resume when a deck-scoped chat opens) without
       *  subscribing a long-lived surface to the whole map. */
      getAll: (): Record<string, SyncChatConversationEntry> => ({
        // Copied, not the live map: this is handed to callers as a snapshot, and
        // a caller that sorts or deletes in place would otherwise mutate the
        // store every subscriber reads — with no write going through sync.
        ...(ops.get() as Record<string, SyncChatConversationEntry>),
      }),
      /** Create a new (empty) conversation. Caller supplies the id (so it can
       *  navigate immediately) and an optional title / tagged deck ids. */
      create: (input: { id: string; title?: string; deckIds?: string[] }) => {
        const now = new Date().toISOString();
        const entry: SyncChatConversationEntry = {
          id: input.id,
          title: input.title ?? '',
          messages: [],
          deckIds: input.deckIds,
          createdTimeISO: now,
          updatedAtISO: now,
        };
        ops.set(entry);
        return entry;
      },
      /** Upsert messages / title / deckIds on a conversation, bumping its ts. */
      save: (
        id: string,
        patch: Partial<Pick<SyncChatConversationEntry, 'messages' | 'title' | 'deckIds'>>,
      ) => {
        const now = new Date().toISOString();
        const base: SyncChatConversationEntry = ops.get()[id] ?? {
          id,
          title: '',
          messages: [],
          createdTimeISO: now,
          updatedAtISO: now,
        };
        ops.set({ ...base, ...patch, updatedAtISO: now });
      },
      /** Rename a conversation. */
      rename: (id: string, title: string) => {
        const existing = ops.get()[id];
        if (!existing) return;
        ops.set({ ...existing, title, updatedAtISO: new Date().toISOString() });
      },
      /** Soft-delete a conversation.
       *
       *  The message payload is blanked (and pushed) FIRST: the server keeps
       *  the last-pushed entry verbatim in its tombstone and re-ships
       *  tombstones to fresh clients on sync-init, so a delete must not
       *  retain the whole thread forever. The microtask hop lets the
       *  dirty-flush send the emptied entry before the delete — both ops in
       *  the same tick would coalesce into a delete of the full entry. */
      remove: (id: string) => {
        const existing = ops.get()[id];
        if (existing && existing.messages.length > 0) {
          ops.set({ ...existing, messages: [] });
          queueMicrotask(() => ops.remove(id));
          return;
        }
        ops.remove(id);
      },
    }),
  }),

  // ── Custom entities (hand-written typed API) ──
  decks: custom<DeckApi>(deckApi),

  // ── Study groups (per-group StudyGroupDo) ──
  //
  // See src/sync/study-groups.ts. The scoped facade lives in its own
  // module so the dashboard / group page / shared deck view can read
  // and mutate SG state without caring about which WebSocket owns the
  // data. The `studyGroups` api is `custom<T>()` because the DX is
  // hand-written — there's no generic entity slice for a facade whose
  // contents change per-group.
  studyGroups: custom<typeof studyGroupsApi>(studyGroupsApi),
});

// Re-exported so app code can read the initial-sync gate through this facade
// (which test suites mock as a unit) instead of deep-importing the provider —
// a deep `provider/sync-core` import in a component drags the whole engine
// module graph into any jest suite that renders it.
export {
  useAccountSeed,
  useHasCompletedInitialSync,
  useSyncOptional,
} from './provider/sync-core';
